import React, {useState} from 'react'
import {Button, Chip, Paper} from '@mui/material'
import {LoaderFunctionArgs, useLoaderData, useNavigate} from 'react-router-dom'
import {DataGrid, GridColDef, GridToolbarContainer, GridToolbarQuickFilter} from '@mui/x-data-grid'
import AddBoxIcon from '@mui/icons-material/AddBox'
import axios from 'axios'
import Typography from '@mui/material/Typography'

export interface CampaignItem {
    id: number
    name: string
    item_code: string
    magic_item_code?: string | null
    image_url?: string | null
    lore?: string | null
    state: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED'
    eventId?: number | null
    dropped_at?: {id: number; name: string} | null
}

export const itemListLoader = async (_args: LoaderFunctionArgs): Promise<CampaignItem[]> => {
    return axios.get('/items/items').then(({data}) => data)
}

const STATE_COLORS: Record<string, 'default' | 'info' | 'success' | 'error'> = {
    DRAFT: 'default',
    SUBMITTED: 'info',
    APPROVED: 'success',
    REJECTED: 'error',
}

const STATE_LABELS: Record<string, string> = {
    DRAFT: 'Draft',
    SUBMITTED: 'Submitted',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
}

const columns: GridColDef[] = [
    {field: 'name', headerName: 'Name', flex: 2, minWidth: 160},
    {field: 'item_code', headerName: 'Item Code', flex: 1, minWidth: 120},
    {
        field: 'state',
        headerName: 'State',
        flex: 1,
        minWidth: 110,
        renderCell: (params) => (
            <Chip
                label={STATE_LABELS[params.value] ?? params.value}
                color={STATE_COLORS[params.value] ?? 'default'}
                size="small"
            />
        ),
    },
    {
        field: 'dropped_at',
        headerName: 'Dropped At',
        flex: 1,
        minWidth: 140,
        valueGetter: (params: any) => params.row.dropped_at?.name ?? '—',
    },
]

function ItemListToolbar({onNew}: {onNew: () => void}) {
    return (
        <GridToolbarContainer sx={{justifyContent: 'space-between', p: 1}}>
            <Button startIcon={<AddBoxIcon/>} variant="contained" size="small" onClick={onNew}>
                New Item
            </Button>
            <GridToolbarQuickFilter/>
        </GridToolbarContainer>
    )
}

export function ItemList() {
    const [items] = useState(useLoaderData() as CampaignItem[])
    const navigate = useNavigate()

    return (
        <Paper elevation={2} sx={{p: 2}}>
            <Typography variant="h5" gutterBottom>Item Manager</Typography>
            <DataGrid
                rows={items}
                columns={columns}
                autoHeight
                pageSizeOptions={[25, 50, 100]}
                initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                slots={{toolbar: () => <ItemListToolbar onNew={() => navigate('new')}/>}}
                onRowClick={(row) => navigate(String(row.id))}
                sx={{cursor: 'pointer'}}
            />
        </Paper>
    )
}
